<template>
  <div
    class="list-item"
    :class="{'list-item--disabled': item.disabled}"
  >
    <div class="body">
      <div
        v-if="allProcessing"
        class="all-processing"
      >
        Processing
      </div>
      <div
        @click="handleDelete"
        class="delete-spu"
      >
        <Icon name="delete-trash" />
      </div>
      <div class="body-l">
        <CartsSelect
          class="check-status"
          :disabled="item.disabled"
          :selected="item.checked"
          @click="handleCheckClick"
        />
        <div class="spu-image">
          <div
            class="quantity"
            v-if="checkedSkuTotalCounts > 0"
          >
            <Icon
              class="prefix"
              name="close-l"
            />
            {{ checkedSkuTotalCounts }}
          </div>
          <Image
            @click="goDetailPage"
            class="image"
            :src="item.imageUrl"
            alt=""
          />
        </div>
      </div>
      <div class="body-r">
        <div class="header">
          <div class="header-info">
            <div
              class="info-desc"
              @click="goDetailPage"
            >
              {{ item.productName }}
            </div>
            <div class="info-variants">
              Selected variants: {{ selectVariants }}
            </div>
          </div>
        </div>
        <p
          class="list-item-error"
          v-if="isBelowMOQ"
        >
          <Icon
            class="list-item-error__icon"
            name="icon-warning-outline"
          />
          The minimum order quantity of {{ item.batchNum }} items has not been met.
        </p>
        <div
          :class="{ 'mgT16': allProcessing }"
          class="sku-list"
        >
          <SkuItem
            v-for="sku in item.skuDetails"
            :key="sku.skuId"
            :item="sku"
            :all-processing="allProcessing"
          />
        </div>
      </div>
    </div>
    <div class="footer">
      <div class="footer-item">
        <div class="label">
          Total Items:
        </div>
        <div class="value">
          <IntlCurrency :currency="currency.current.targetCurrency" />
          {{ totalItemsPrice }}
        </div>
      </div>
      <div class="footer-item">
        <div class="label">
          {{ hasProcessingInChecked ? 'Est.Shipping:' : 'Shipping Cost:' }}
        </div>
        <div class="value">
          <IntlCurrency :currency="currency.current.targetCurrency" />
          {{ shippingCost }}
        </div>
      </div>
      <div class="footer-item">
        <div class="label">
          {{ hasProcessingInChecked ? 'Est.Subtotal:' : 'Subtotal:' }}
        </div>
        <div class="value">
          <IntlCurrency :currency="currency.current.targetCurrency" />
          {{ subtotal }}
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { Icon } from '@/components/Icon'
import { IntlCurrency } from '@/components/NewIntlCurrency'
import { useCurrencyStore } from '@/pinia/currency'
import SkuItem from './SkuItem.vue'
import CartsSelect from '@/views/Carts/components/CartsSelect.vue'
import { useCartSpu } from '@/views/Carts/hooks/useCartSpu'
import { buriedPointUtil } from '@/utils/utils'
import { Image } from '@/components/Image'

const props = defineProps({
  item: {
    type: Object,
    default: () => ({})
  }
})

const { allProcessing, isBelowMOQ, hasProcessingInChecked, checkedSkuTotalCounts, selectVariants, totalItemsPrice, shippingCost, subtotal, handleCheck, handleDelete } = useCartSpu(computed(() => props.item))

const router = useRouter()

const currency = useCurrencyStore()

const handleCheckClick = () => {
  buriedPointUtil({ eventId: 'PCL202', type: 'SPU' })
  handleCheck()
}

const goDetailPage = () => {
  const { href } = router.resolve({
    path: `/offer/${props.item.offerId}`
  })

  window.open(href, '_blank')
}

</script>

<style lang='less' scoped>
.list-item {
  border: 1px solid #ebf1f5;
  border-radius: 8px;
  margin-bottom: 16px;

  &--disabled .header-info {
    filter: grayscale(1) opacity(.5);
  }

  .body {
    position: relative;
    display: flex;
    align-items: flex-start;
    gap: 16px;
    padding: 24px 16px 16px;

    @media (min-width: 1280px) {
      padding: 24px 24px 16px;
    }

    .all-processing {
      position: absolute;
      top: 0;
      right: 0;
      width: 67px;
      height: 18px;
      line-height: 18px;
      text-align: center;
      border-radius: 10px;
      background-color: #ffa033;
      color: white;
      font-size: 10px;
      font-family: mon-600;
    }

    .delete-spu {
      position: absolute;
      top: 30px;
      right: 24px;
      width: 24px;
      height: 24px;
      line-height: 24px;
      text-align: center;
      font-size: 24px;
      cursor: pointer;
      color: #6E717A;
    }

    .body-l {
      display: flex;
      align-items: center;
      gap: 12px;
      .check-status {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 20px;
        height: 20px;
        font-size: 20px;
        cursor: pointer;
      }

      .spu-image {
        position: relative;
        width: 80px;
        height: 80px;
        border: 1px solid #EBF1F5;
        border-radius: 8px;

        .quantity {
          z-index: 1;
          position: absolute;
          top: -4px;
          right: -8px;
          height: 16px;
          padding: 0 6px;
          border-radius: 8px;
          background-color: rgba(0, 0, 0, .8);
          color: white;
          font-size: 12px;
          font-family: mon-500;
          display: flex;
          align-items: center;
          outline: 1px solid white;
          .prefix {
            font-size: 8px;
          }
        }
        .image {
          width: 100%;
          height: 100%;
          object-fit: contain;
          border-radius: 8px;
          cursor: pointer;
        }
      }
    }

    .body-r {
      flex: 1;
      .header {
        display: flex;
        justify-content: space-between;
        .header-info {
          .info-desc {
            margin-right: 40px;
            color: #232323;
            font-size: 13px;
            font-family: mon-500;
            line-height: 18px;

            &:hover {
              color: #004F9A;
              cursor: pointer;
              text-decoration: underline;
            }
          }
          .info-variants {
            margin-top: 8px;
            color: #5c5c5c;
            font-size: 12px;
            font-family: mon-500;
          }
        }
      }
      .sku-list {
        &.mgT16 {
          margin-top: 16px;
        }
      }
    }
  }

  .footer {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: end;
    height: 62px;
    padding: 0 16px;

    @media (min-width: 1280px) {
      padding: 0 24px;
    }

    &::after {
      content: '';
      position: absolute;
      top: 0;
      left: 24px;
      right: 24px;
      height: 1px;
      background-color: #ebf1f5;
    }

    &-item__discount {
      width: 124px;

      @media (min-width: 1160px) {
        margin-right: 8px;
        width: auto;
      }
    }

    .footer-item {
      display: flex;
      align-items: center;
      gap: 4px;
      margin-left: 24px;
      line-height: 18px;
      .label {
        color: #6E717A;
        font-size: 14px;
        font-family: mon-500;
      }
      .value {
        color: #232323;
        font-size: 14px;
        font-family: mon-600;
      }
    }
  }

  &-error {
    margin-top: 8px;
    font-family: mon-500;
    font-size: 12px;
    line-height: 16px;
    color: #7A0505;

    &__icon {
      font-size: 14px;
    }

    &__contact {
      margin-left: 6px;
      text-decoration: underline;
      color: #232323;
      cursor: pointer;

      &:hover {
        color: #004F9A;
      }
    }
  }
}
</style>
